/* eslint-disable @typescript-eslint/naming-convention */
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { Option } from '@components/MatchEventEditor/utils'

import { useSearchState } from '@hooks/useSearchState'
import { useZodForm } from '@hooks/useZodForm'

import {
    EventFormData,
    EventSchema,
    MatchEventEditorEventType,
    MatchEventEditorFixtureExtraInfo,
    MatchEventPeriodNumber,
    TMatchEventEditorEventType,
} from '@common/types'

const EVENT_TYPES_WITH_SCORE = new Set<TMatchEventEditorEventType>([
    MatchEventEditorEventType.goal,
    MatchEventEditorEventType.penalty_missed,
    MatchEventEditorEventType.penalty_shootout_event_deep_coverage,
])

const PERIOD_EXTRA_INFO_WITH_SCORE = new Set<number>([
    MatchEventEditorFixtureExtraInfo.half_time,
    MatchEventEditorFixtureExtraInfo.finish_after_regular_time,
    MatchEventEditorFixtureExtraInfo.finish_after_extra_time,
    MatchEventEditorFixtureExtraInfo.finish_after_penalty_shootout,
])

const MatchEditorFormContext = createContext<{
    resetForm: (newValues?: Partial<EventFormData> | undefined, keepDirtyValues?: boolean) => void
    fakePlayers: Option[]
    createFakePlayer: (inputValue: string, fieldName: 'player1_id' | 'player2_id') => Promise<void>
    isCreatingPlayer: boolean
    zodForm: UseFormReturn<EventFormData>
    isPlayersError: () => boolean
    isExtraInfoRequired: boolean
    isPenaltyOrderRequired: boolean
    isResultRequired: boolean
    isFormValid: boolean
    isAdditionalTimeSelected: boolean
    setIsAdditionalTimeSelected: (isSelected: boolean) => void
    isFormDisabled: boolean
    setIsFormDisabled: (isSelected: boolean) => void
} | null>(null)

const initialDefaultValues: EventFormData = {
    team_id: { value: '', label: '' },
    type: { value: '', label: '' },
    time: {
        minutes: 0,
        seconds: 0,
        additionalMinutes: '0',
    },
    player1_id: { value: '', label: '' },
    player2_id: { value: '', label: '' },
    team1_score: 0,
    team2_score: 0,
    period_number: { value: '', label: '' },
    extra_info: 0,
    penalty_order: 1,
}

export function MatchEditorFormContextProvider({ children }) {
    const [{ matchId: selectedMatchId }] = useSearchState()
    const [isAdditionalTimeSelected, setIsAdditionalTimeSelected] = useState(false)
    const [isFormDisabled, setIsFormDisabled] = useState(false)

    const [isCreatingPlayer, setIsCreatingPlayer] = useState(false)
    const [fakePlayers, setFakePlayers] = useState<Option[]>([])

    const zodForm = useZodForm({
        schema: EventSchema,
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: initialDefaultValues,
    })

    const resetForm = useCallback(
        (newValues?: Partial<EventFormData>, keepDirtyValues = false) => {
            if (newValues) {
                zodForm.reset({ ...initialDefaultValues, ...newValues }, { keepDirtyValues })

                return
            }

            const resetValues = {
                ...initialDefaultValues,
            }

            zodForm.reset(resetValues, { keepDirtyValues: false })
        },
        [zodForm],
    )

    // Reset on match changes
    useEffect(() => {
        resetForm()

        setFakePlayers([]) // Reset fake players as we can't map them to API fake players
    }, [selectedMatchId, resetForm])

    const createFakePlayer = async (inputValue: string, fieldName: 'player1_id' | 'player2_id') => {
        setIsCreatingPlayer(true)

        const newFakePlayer = {
            value: `is:fake:player:${fakePlayers.length + 1}`,
            label: inputValue,
        }

        if (!fakePlayers.find(fakePlayer => fakePlayer.value === newFakePlayer.value)) {
            setFakePlayers(currentFakePlayers => [newFakePlayer, ...currentFakePlayers])
        }

        // TODO: Ask about the reason for having this
        await new Promise(resolve => {
            setTimeout(resolve, 100)
        })

        zodForm.setValue(fieldName, newFakePlayer, { shouldDirty: true })

        setIsCreatingPlayer(false)
    }

    const selectedEventTypeOption = zodForm.watch('type')
    const player1Value = zodForm.watch('player1_id')
    const player2Value = zodForm.watch('player2_id')
    const period = zodForm.watch('period_number')
    const extraInfo = zodForm.watch('extra_info')

    const eventType = Number(selectedEventTypeOption?.value) as TMatchEventEditorEventType

    const isVAR = eventType === MatchEventEditorEventType.video_assistant_referee_over
    const isPenaltyShootoutGoal =
        eventType === MatchEventEditorEventType.goal && Number(period.value) === MatchEventPeriodNumber.penalties

    const isPenaltyShootoutMiss =
        eventType === MatchEventEditorEventType.penalty_shootout_event_deep_coverage &&
        Number(period.value) === MatchEventPeriodNumber.penalties

    const isPenaltyOrderRequired = isPenaltyShootoutGoal || isPenaltyShootoutMiss

    const isEventTypeWithScore = EVENT_TYPES_WITH_SCORE.has(eventType)

    const isPeriodEventWithScore =
        eventType === MatchEventEditorEventType.fixture_status && PERIOD_EXTRA_INFO_WITH_SCORE.has(Number(extraInfo))

    const isResultRequired = isEventTypeWithScore || isPeriodEventWithScore

    const isPlayersError = () =>
        eventType === MatchEventEditorEventType.substitution ? !player1Value?.value !== !player2Value?.value : false

    const isFormValid = Object.keys(zodForm.formState.errors).length === 0 && !isPlayersError()

    const isExtraInfoRequired = !isVAR && !isPenaltyShootoutGoal

    return (
        <MatchEditorFormContext.Provider
            value={{
                resetForm,
                fakePlayers,
                createFakePlayer,
                isCreatingPlayer,
                zodForm,
                isFormValid,
                isPlayersError,
                isExtraInfoRequired,
                isPenaltyOrderRequired,
                isResultRequired,
                isAdditionalTimeSelected,
                setIsAdditionalTimeSelected,
                isFormDisabled,
                setIsFormDisabled,
            }}>
            {children}
        </MatchEditorFormContext.Provider>
    )
}

export function useMatchEditorFormContext() {
    const context = useContext(MatchEditorFormContext)

    if (!context) {
        throw new Error('useMatchEditorFormContext must be used within a MatchEditorFormContextProvider')
    }

    return context
}
